import { isValidationCode, type ApiValidationIssue } from "@repo/schema";
import i18n, { type ParseKeys } from "i18next";
import type { I18nKey } from "./tKey";

/**
 * The fields of a Zod issue the wording depends on, whether it comes raw from
 * Zod (the error map) or serialised by the API (`ApiErrorBody.errors`).
 */
type Issue = { code?: string; [field: string]: unknown };

const asNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

/**
 * The `validation` catalog's text for a Zod issue in the active language.
 *
 * Issues tagged with a `ValidationCode` (field-specific copy from
 * `issueMessage`, business-rule refines) read `custom.<code>`; everything else
 * is worded by issue kind. Undefined for an untagged `custom` issue, whose own
 * English message is the best text there is.
 */
export const validationMessage = (issue: Issue): string | undefined => {
  const t = i18n.getFixedT(null, "validation");
  const params = (issue.params ?? {}) as Record<string, unknown>;
  const minimum = asNumber(issue.minimum);
  const maximum = asNumber(issue.maximum);

  if (isValidationCode(params.code)) {
    const key = `custom.${params.code}` as ParseKeys<"validation">;
    return t(key, { ...params, count: minimum ?? maximum });
  }

  switch (issue.code) {
    case "invalid_type":
      // Only a raw issue carries `input`; the API strips it.
      if ("input" in issue && issue.input == null) return t("required");
      switch (issue.expected) {
        case "number":
          return t("type.number");
        case "int":
          return t("type.int");
        case "string":
          return t("type.string");
        case "boolean":
          return t("type.boolean");
      }
      return t("invalid");
    case "too_small":
      switch (issue.origin) {
        case "string":
          return minimum === 1 ? t("required") : t("string.tooShort", { count: minimum });
        case "number":
          return issue.inclusive === false
            ? t("number.greaterThan", { value: minimum })
            : t("number.min", { value: minimum });
        case "array":
        case "set":
          return t("items.tooFew", { count: minimum });
      }
      return t("invalid");
    case "too_big":
      switch (issue.origin) {
        case "string":
          return t("string.tooLong", { count: maximum });
        case "number":
          return issue.inclusive === false
            ? t("number.lessThan", { value: maximum })
            : t("number.max", { value: maximum });
        case "array":
        case "set":
          return t("items.tooMany", { count: maximum });
      }
      return t("invalid");
    case "invalid_format":
      if (issue.format === "email") return t("format.email");
      if (issue.format === "url") return t("format.url");
      return t("format.other");
    case "not_multiple_of":
      return t("number.multipleOf", { value: issue.divisor });
    case "invalid_value":
      return t("invalidOption");
    case "custom":
      return undefined;
    default:
      return t("invalid");
  }
};

/**
 * A `VALIDATION_FAILED` body's issues as one line, `path: message; …`, for the
 * first three. Paths are the API's field names, which is what a developer
 * reading a forced 400 needs; forms validate the same schema client-side first.
 */
export const validationSummary = (issues: ApiValidationIssue[]): string =>
  issues
    .slice(0, 3)
    .map((issue) => {
      const text = validationMessage(issue) ?? issue.message;
      const path = issue.path.join(".");
      return path ? `${path}: ${text}` : text;
    })
    .join("; ");

/**
 * `{ error }` for a web-local schema whose message lives in a module
 * namespace: `z.string().min(1, issueKey(tKey("evaluation:form.nameRequired")))`.
 * Resolved when the schema runs, so it follows the active language.
 */
export const issueKey = (key: I18nKey) => ({
  error: () => i18n.t(key),
});
